import React, { useCallback, useEffect, useState } from 'react';
import { Smartphone, LogOut } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../ui/table';
import { Skeleton } from '../ui/skeleton';
import communityRegistryApi, { AppSessionOut } from '../../services/communityRegistryApi';

const formatStarted = (iso?: string) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

/**
 * Lists every account user currently logged into the Android app (never web —
 * `active_session_token` is only ever set on a client_type=="mobile" login)
 * and lets a super admin terminate any one of them, including their own.
 */
const AppSessionsTab: React.FC = () => {
  const [items, setItems] = useState<AppSessionOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [terminatingId, setTerminatingId] = useState<number | null>(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    communityRegistryApi
      .listAppSessions()
      .then((res) => {
        setItems(res.items);
        setError(null);
      })
      .catch((e) => setError(e?.response?.data?.detail || 'Failed to load app sessions'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTerminate = async (row: AppSessionOut) => {
    const confirmed = window.confirm(
      row.is_self
        ? 'Log your own Android app session out on its device?'
        : `Log out ${row.name}'s Android app session on their device?`,
    );
    if (!confirmed) return;
    setTerminatingId(row.user_id);
    try {
      await communityRegistryApi.terminateAppSession(row.user_id);
      // Re-fetch rather than optimistic-only remove: if the device silently
      // re-authenticated, the row should come back; otherwise it stays gone.
      fetchData();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to terminate session');
    } finally {
      setTerminatingId(null);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Smartphone className="h-4 w-4" />
              Active app sessions
            </CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              Accounts currently signed into the Community Registry Android app. Terminate clears the server
              lock — the device is forced to the login screen on its next request or when the app is resumed.
              Web logins are never shown or affected here.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData}>
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Account</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Session started</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading &&
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 4 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-sm text-muted-foreground">
                    No one is currently logged into the app.
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                items.map((row) => (
                  <TableRow key={row.user_id}>
                    <TableCell className="font-medium">
                      {row.name}
                      <div className="text-xs font-normal text-muted-foreground">{row.email}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {row.role.replace('_', ' ')}
                      </Badge>
                      {row.is_self && (
                        <Badge variant="outline" className="ml-2">
                          You
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>{formatStarted(row.session_started_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="destructive"
                        size="sm"
                        disabled={terminatingId === row.user_id}
                        onClick={() => handleTerminate(row)}
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        {terminatingId === row.user_id ? 'Terminating…' : 'Terminate'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AppSessionsTab;
